import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { CacheKeys } from '@/lib/cache'
import { type ConfirmInfoState, setConfirmInfoState } from '@/models/confirmInfo'
import { fillApplicationError, fillApplicationErrorMessage } from '@/models/fillApplication'

/** Runs one of Confirmation's steps and shows the state it comes back with; a refusal stays on screen. */
export function useConfirmInfoStep(orgId: string) {
  const queryClient = useQueryClient()
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [refusedCode, setRefusedCode] = useState<string | null>(null)

  async function run(step: () => Promise<ConfirmInfoState>): Promise<boolean> {
    setPending(true)
    setError(null)
    setRefusedCode(null)
    try {
      setConfirmInfoState(queryClient, orgId, await step())
      return true
    } catch (failure) {
      setError(fillApplicationErrorMessage(failure))
      setRefusedCode(fillApplicationError(failure)?.code ?? null)
      // The server may have moved on (a check changed, someone else sent it): show where it stands.
      await queryClient.invalidateQueries({ queryKey: [CacheKeys.FILL_APPLICATION_STATE, orgId] })
      return false
    } finally {
      setPending(false)
    }
  }

  return { run, pending, error, refusedCode }
}
